import { useContext, useEffect, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from '@fullcalendar/daygrid';
import bootstrap5Plugin from '@fullcalendar/bootstrap5';
import moment from 'moment';
import { t } from "i18next";
import trainingIcon from "../../assets/images/trainings.svg";
import { Link, useNavigate } from "react-router-dom";
import esLocale from '@fullcalendar/core/locales/es';
import { LoadingSpinner } from "../general/loading-spinner";
import { getAthletePlanning } from "../../services/planning";
import { UserContext } from "../../App";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Feedback } from "../pages/athlete/training/feedback";

export function AthleteMiniCalendarBlock() {
    const navigate = useNavigate();

    const { userProfile } = useContext(UserContext);

    const [events, setEvents] = useState<any[]>([]);
    const [planning, setPlanning] = useState<any[]>([]);
    const [selectedFeedbackEvent, setSelectedFeedbackEvent] = useState<any>();
    const [showFeedback, setShowFeedback] = useState(false);
    const [filteredEvents, setFilteredEvents] = useState<any[]>([]);
    const [currentMonth, setCurrentMonth] = useState(moment());
    const isLoadingRef = useRef(false);

    const renderTooltip = (props: any) => (
        <Tooltip class="event-tooltip" {...props}>
            <div className="time">
                { props.event._def.extendedProps.day_moment == 'morning' ? t("Morning session") : t("Afternoon session") }
            </div>
            <div className="tooltip-content">
                <div className="team-name">{ props.event._def.extendedProps.team_name}</div>
                <div className="event-title">{ props.event.title }</div>
            </div>
            <div className="feedback">
                <a className="btn btn-primary" data-team={ props.event._def.extendedProps.team_id }
                    data-day={ props.event._def.extendedProps.day } data-moment={ props.event._def.extendedProps.day_moment }
                    data-session={ props.event._def.extendedProps.day_moment_session } onClick={ handleShowFeedback }>
                    { t("Feedback") }
                </a>
            </div>
        </Tooltip>
    );

    const handleShowFeedback = (event: any) => {
        event.preventDefault();

        const index = planning.findIndex((p: any) => p.team_id == event.currentTarget.dataset.team && p.datetime == event.currentTarget.dataset.day &&
            p.day_moment == event.currentTarget.dataset.moment && p.day_moment_session == event.currentTarget.dataset.session);
        if (index !== -1) {
            setSelectedFeedbackEvent(planning[index]);
            setShowFeedback(true);
        } else {
            setSelectedFeedbackEvent(Object.assign({}));
        }
        
    }

    function eventContent(arg: any) {
        /*const handleEventClick = (event: any) => {
            event.preventDefault();
            navigate(arg.event.url);
        }*/
        return (
             <OverlayTrigger placement="top" delay={{ show: 250, hide: 1000 }} overlay={renderTooltip(arg)}>
                <span className={ arg.event.classNames } /*href="#" onClick={ handleEventClick} title={ arg.event.title}*/></span>
            </OverlayTrigger>);
    }

    // Función para cargar eventos de un rango de fechas específico
    const loadEventsForMonth = (month: moment.Moment) => {
        // Evitar llamadas duplicadas
        if (isLoadingRef.current) {
            return;
        }

        isLoadingRef.current = true;
        const startDate = month.clone().startOf('month').format('YYYY-MM-DD');
        const endDate = month.clone().endOf('month').format('YYYY-MM-DD');

        getAthletePlanning(userProfile.reference_id, startDate, endDate)
            .then(response => {
                setPlanning(response.data);

                const mappedEvents = response.data.map((e: any) => {
                    return {
                        id: e.datetime + '-' + e.day_moment + '-' + e.day_moment_session + '-' + e.team_id,
                        title: e.description ?? 'Sesión de entrenamiento (' + e.datetime + ')',
                        date: moment(e.datetime).format('YYYY-MM-DD'),
                        time: moment(e.datetime).format('HH:mm:ss'),
                        season_id: e.season_id,
                        team_id: e.team_id,
                        team_name: e.team_name,
                        day: e.datetime,
                        day_moment: e.day_moment,
                        day_moment_session: e.day_moment_session,
                        url: '',
                        color: e.color
                    }
                });
                setEvents(mappedEvents);
                setFilteredEvents(mappedEvents);
                isLoadingRef.current = false;
            })
            .catch(error => {
                console.error('Error loading athlete planning:', error);
                isLoadingRef.current = false;
            });
    };

    useEffect(() => {
        loadEventsForMonth(currentMonth);
    }, []);

    const handleCalendarChange = (dateInfo: any) => {
        // Detectar el mes principal que se está mostrando
        const viewDate = moment(dateInfo.start);
        const viewEnd = moment(dateInfo.end);
        const middleDate = moment(viewDate).add(Math.floor(viewEnd.diff(viewDate, 'days') / 2), 'days');
        const newMonth = middleDate.clone().startOf('month');

        // Solo cargar eventos si el mes cambió
        if (!newMonth.isSame(currentMonth, 'month')) {
            setCurrentMonth(newMonth);
            loadEventsForMonth(newMonth);
        }
    }

    const handleFeedbackOnClose = () => {
        setSelectedFeedbackEvent(Object.assign({}));
        setShowFeedback(false);
    }

    return (
        <div className="block mini-calendar-container">
            <div className="block-inner">
                <h3 className="block-title">
                    <img src={ trainingIcon } alt={ t("trainings")} />
                    { t("trainings") }
                </h3>
                <FullCalendar
                    plugins={[ dayGridPlugin, bootstrap5Plugin ]}
                    initialView="dayGridMonth"
                    weekends={true}
                    firstDay={ 1 }
                    locale={esLocale}
                    height={ 'auto' }
                    events={ events }
                    eventContent= { eventContent }
                    datesSet={ handleCalendarChange }
                />
                <div className="events-list">
                    {
                        filteredEvents.map((e: any) => {
                            return <div key={ 'event-' + e.id } className="event-item">
                                    <div className="color-mark" style={{backgroundColor: e.color}}></div>
                                    <div className="name">
                                        <Link to={ '/athlete/planning/session/' + e.season_id + '/' + e.team_id + '/' + e.date + '/' + e.day_moment + '/' + e.day_moment_session } title={ e.title }>
                                            { e.title }
                                        </Link>
                                    </div>
                                    <div className="date">
                                        { moment(e.id, 'YYYY-MM-DD HH:mm:ss').format('D MMMM') + ' (' + (e.day_moment == 'morning' ? t("Morning session") : t("Afternoon session")) + ')' }
                                    </div>
                                </div>
                        })
                    }
                </div>
            </div>
            { showFeedback && selectedFeedbackEvent &&
                <Feedback event={ selectedFeedbackEvent } onClose={ handleFeedbackOnClose } />
            }
        </div>
    );
}  
